/**
 * useCompetitionRegistry — who is entered, in what role, and who pays.
 *
 * Registrations for real users (competitor / judge / organiser), guest
 * competitors registered by proxy, the billing identity behind a guest entry,
 * and the start-number backfill. Two of these paths go through Cloud Functions
 * rather than writing directly, because the entry itself is transactional:
 * session capacity, age gates and the registration window are all decided
 * server-side.
 */
import { useCallback } from "react";
import {
  collection, doc, setDoc, updateDoc, getDocs, getDoc,
  writeBatch, runTransaction, serverTimestamp, query, where,
} from "firebase/firestore";
import { db } from "../../firebase";
import { lowestFreeStartNumber } from "@sendly/shared";
import { PAYMENT_METHODS } from "../../competitionUtils";
import { generateGuestId } from "../../utils/guestUtils";
import { callableErrorMessage } from "../../utils/callableError";
import { fetchCompParts } from "../../utils/competitionFetch";
import { compareNames } from "@sendly/shared";
import { callableRef } from "../../utils/callFunction";
import { buildJudgeRegistration, buildOrganizerRegistration } from "../../competition/staffRegistration";

const registerForCompetitionCF = callableRef("registerForCompetition");
const updateCompetitionRegistrationCF = callableRef("updateCompetitionRegistration");

/**
 * Callable errors arrive wrapped ("functions/failed-precondition"). Unwrap to
 * the bare machine code so the modals can map SESSION_FULL / AGE_* / the
 * registration-window codes onto i18n. The code is read from `details.code`
 * first: the SDK appends " [400]" to the message (firebase 12.19), so the
 * message only serves as the fallback for a server that sends no details.
 */
export function unwrapCallableError(err, fallback) {
  const code = String(err?.code || err?.details?.code || "").replace(/^functions\//, "");
  if (code === "failed-precondition" || code === "invalid-argument" || code === "permission-denied") {
    return new Error(callableErrorMessage(err, fallback));
  }
  // Contention, not a verdict. The registration transaction reads the whole
  // registrations collection, so a stampede serializes; when the ten retries
  // run out Firestore throws ABORTED, which a callable flattens to `internal`
  // with the literal message "INTERNAL". Showing that to someone at the desk
  // told them nothing and implied their entry was rejected on the merits.
  if (code === "internal" || code === "aborted" || code === "deadline-exceeded"
      || code === "unavailable" || code === "cancelled") {
    console.error("Competition registration CF contention/transport error:", err);
    return new Error("REGISTRATION_BUSY");
  }
  console.error("Competition registration CF error:", err);
  return new Error(callableErrorMessage(err, fallback));
}

export function useCompetitionRegistry({ gymId, authUser, withActor, deleteAttributed }) {

  const registerCompetitor = useCallback(async (compId, uid, data) => {
    if (!gymId) return;
    try {
      const res = await registerForCompetitionCF({
        gymId,
        compId,
        uid,
        displayName: data.displayName || "",
        groupId: data.groupId || null,
        sessionId: data.sessionId || null,
        billing: data.billing || null,
        isGuest: false,
      });
      // The callable's answer carries the early-bird snapshot the entry got
      // (`earlyBird`: { tierId, discountPct, source } | null), so the caller
      // reports the SERVER's decision, not its own clock's.
      return res?.data || null;
    } catch (err) {
      throw unwrapCallableError(err, "Registration failed");
    }
  }, [gymId]);

  // The staff shapes are the pure builders in competition/staffRegistration.js
  // (the E2E seed appoints judges with the same ones); only the timestamp and
  // the actor stamp are this hook's.
  const registerJudge = useCallback(async (compId, uid, data) => {
    if (!gymId) return;
    await setDoc(doc(db, "gyms", gymId, "competitions", compId, "registrations", uid), withActor({
      ...buildJudgeRegistration({ uid, displayName: data.displayName, isOrganizer: data.isOrganizer, sessionIds: data.sessionIds }),
      registeredAt: serverTimestamp(),
    }));
  }, [gymId, withActor]);

  const registerOrganizer = useCallback(async (compId, uid, data) => {
    if (!gymId) return;
    await setDoc(doc(db, "gyms", gymId, "competitions", compId, "registrations", uid), withActor({
      ...buildOrganizerRegistration({ uid, displayName: data.displayName }),
      registeredAt: serverTimestamp(),
    }));
  }, [gymId, withActor]);

  const unregister = useCallback(async (compId, uid) => {
    if (!gymId) return;
    await deleteAttributed(doc(db, "gyms", gymId, "competitions", compId, "registrations", uid));
  }, [gymId, deleteAttributed]);

  /**
   * Admin/organizer field edit on any registration (role, start number,
   * exclusion flag, …). Goes straight to Firestore — the rules gate it by
   * role. NOT for group/session moves: those are capacity- and age-checked,
   * which rules cannot do, so they go through `editRegistration` below.
   */
  const updateRegistration = useCallback(async (compId, uid, data) => {
    if (!gymId) return;
    await updateDoc(doc(db, "gyms", gymId, "competitions", compId, "registrations", uid), withActor(data));
  }, [gymId, withActor]);

  /**
   * Change an existing registration's group / session (and a guest's details)
   * through the CF that re-runs the capacity, age-cohort and
   * registration-window checks. `groupId` and `sessionId` are denied to
   * clients in the security rules, so this is the only path that moves them —
   * for the competitor themselves, for a parent editing their guest, and for
   * staff alike.
   */
  const editRegistration = useCallback(async (compId, uid, data) => {
    if (!gymId) return;
    try {
      const res = await updateCompetitionRegistrationCF({ gymId, compId, uid, ...data });
      // `{ success, earlyBird }` — the early-bird snapshot after a staff override.
      return res?.data || null;
    } catch (err) {
      throw unwrapCallableError(err, "Registration update failed");
    }
  }, [gymId]);

  const updateEntryFeeStatus = useCallback(async (compId, uid, paid, paidAmount) => {
    if (!gymId) return;
    const data = { entryFeePaid: paid };
    if (paidAmount != null) data.paidAmount = paidAmount;
    // Use setDoc+merge so it works even if the registration doc doesn't exist yet (e.g. unregistered parent with guests)
    await setDoc(doc(db, "gyms", gymId, "competitions", compId, "registrations", uid), withActor(data), { merge: true });
  }, [gymId, withActor]);

  /**
   * How the entry fee was handed over — card / transfer / cash, or null to
   * clear it. Separate from `updateEntryFeeStatus` because the two are edited
   * at different moments: the amount is typed once, the method is a one-click
   * change the organiser may correct afterwards without re-entering the sum.
   *
   * Admin/organizer only — `paymentMethod` is on the registration rule's
   * self-update denylist, so a competitor writing their own would be rejected.
   */
  const updatePaymentMethod = useCallback(async (compId, uid, method) => {
    if (!gymId) return;
    const value = PAYMENT_METHODS.includes(method) ? method : null;
    await setDoc(
      doc(db, "gyms", gymId, "competitions", compId, "registrations", uid),
      withActor({ paymentMethod: value }),
      { merge: true }
    );
  }, [gymId, withActor]);

  // ── Guest competitors ──

  /**
   * Record the caller's billing identity for a competition. One billing record
   * per user per competition, however many guests they bring — the invoice goes
   * to the person, not to each guest.
   *
   * TWO documents, because they answer to different audiences:
   *
   *   • `registrations/{uid}` — the payer STUB. World-readable, so it carries no
   *     identity: just `uid` and the actor stamp. It has to exist even for a
   *     parent who never enters themselves, because the money fields
   *     (`paidAmount` / `paymentMethod` / `entryFeePaid`) hang off it and
   *     `buildPaidUidSet` looks the payer up by it.
   *   • `registrationContacts/{uid}` — the identity itself, behind the gated
   *     rule. See the `registrationContacts` block in firestore.rules.
   *
   * `setDoc(merge)` on both because neither may exist yet. Called BEFORE
   * registerGuestCompetitor so the registration CF finds the stub and folds its
   * entry-fee rollup into it.
   */
  const saveParentBilling = useCallback(async (compId, billing) => {
    if (!gymId || !authUser?.uid || !billing) return;
    const base = ["gyms", gymId, "competitions", compId];
    await Promise.all([
      setDoc(
        doc(db, ...base, "registrations", authUser.uid),
        withActor({ uid: authUser.uid }),
        { merge: true }
      ),
      setDoc(
        doc(db, ...base, "registrationContacts", authUser.uid),
        withActor({ ...billing }),
        { merge: true }
      ),
    ]);
  }, [gymId, authUser, withActor]);

  /**
   * Everything the billing Excel needs, fetched fresh rather than taken from
   * the live listeners — the export must reflect persisted data whichever
   * competition happens to be the active one (R-CE-29 pattern).
   *
   * `contacts` is a one-shot `getDocs`, NOT a listener: an entrant's invoice
   * identity is not real-time data, and a standing listener on it would stream
   * the gym's whole PII set into memory for a 500-entrant competition that
   * mostly never opens the export. Gated by the `registrationContacts` list
   * rule, so this resolves only for admins, organisers and `competitions.manage`
   * holders — the same circle the export button is behind.
   */
  const loadBillingExportData = useCallback(async (compId) => {
    const { registrations, groups, registrationContacts } = await fetchCompParts(
      gymId, compId, ["registrations", "groups", "registrationContacts"]);
    return { registrations, groups, contacts: registrationContacts };
  }, [gymId]);

  /**
   * One entrant's contact doc, or null. Used by the registration form to
   * prefill what the payer typed last time and what an organiser recorded about
   * a guest. Reads resolve for the entrant themselves, the parent who brought
   * the guest, and staff — see the `get` rule.
   */
  const loadRegistrationContact = useCallback(async (compId, regId) => {
    if (!gymId || !compId || !regId) return null;
    try {
      const snap = await getDoc(doc(db, "gyms", gymId, "competitions", compId, "registrationContacts", regId));
      return snap.exists() ? snap.data() : null;
    } catch {
      // A permission error here is not worth failing the form over: the caller
      // falls back to the transitional copy still on the registration doc.
      return null;
    }
  }, [gymId]);

  /**
   * Remember the caller's billing identity on their global profile so the next
   * competition starts prefilled. Fire-and-forget on purpose: a failed profile
   * write must never fail the registration that triggered it.
   */
  const saveBillingToProfile = useCallback((billing) => {
    if (!authUser?.uid || !billing) return;
    updateDoc(doc(db, "users", authUser.uid), { billingInfo: billing }).catch(() => {});
  }, [authUser]);

  /**
   * `bulkOp` = `{ id, count }` groups the guests of one bulk operation (the
   * Excel start-list import) so the audit trigger writes a single summary entry
   * rather than one per imported competitor.
   */
  const registerGuestCompetitor = useCallback(async (compId, data, bulkOp) => {
    if (!gymId || !authUser) return null;
    const guestId = generateGuestId();
    try {
      const result = await registerForCompetitionCF({
        gymId,
        compId,
        uid: guestId,
        bulkOp: bulkOp || null,
        displayName: data.displayName || "",
        groupId: data.groupId || null,
        sessionId: data.sessionId || null,
        isGuest: true,
        proxyUid: data.proxyUid || null,
        birthYear: data.birthYear || null,
        club: data.club || null,
        notes: data.notes || null,
        // Only an organizer-added guest carries billing of their own: a proxy
        // guest is invoiced on their parent's doc, written before this call.
        billing: data.billing || null,
      });

      return result.data?.registrationId || guestId;
    } catch (err) {
      throw unwrapCallableError(err, "Guest registration failed");
    }
  }, [gymId, authUser]);

  /**
   * Edit a guest competitor. Same CF as the self-edit: a guest's group and
   * session are capacity- and age-gated exactly like anyone else's, whether
   * the editor is the parent who brought them or an organizer.
   */
  const updateGuestCompetitor = useCallback(async (compId, guestId, updates) => {
    if (!gymId) return;
    try {
      await updateCompetitionRegistrationCF({ gymId, compId, uid: guestId, ...updates });
    } catch (err) {
      throw unwrapCallableError(err, "Guest update failed");
    }
  }, [gymId]);

  /**
   * Remove a guest competitor. The payer's billing is NOT recalculated here:
   * the `recalcPayerBillingOnGuestDelete` trigger does it server-side, the
   * mirror image of the rollup the registration CF writes when a guest is
   * added.
   *
   * It used to be a client-side recompute right here, and it could not work:
   * the write carried `entryFeePaid`, which is on the registration rules'
   * self-update denylist, so for the caller who triggers this most — a parent
   * removing their own guest — the whole update was rejected. It failed inside
   * a try/catch, so the only trace was a console line.
   */
  const unregisterGuest = useCallback(async (compId, guestId) => {
    if (!gymId) return;

    // Hard delete: remove registration + all attempts (including audit subcollections)
    await deleteAttributed(doc(db, "gyms", gymId, "competitions", compId, "registrations", guestId));
    const attemptsQuery = query(
      collection(db, "gyms", gymId, "competitions", compId, "attempts"),
      where("uid", "==", guestId)
    );
    const snap = await getDocs(attemptsQuery);
    if (snap.size > 0) {
      // Delete audit subcollections under each attempt first
      for (const attemptDoc of snap.docs) {
        const auditSnap = await getDocs(collection(attemptDoc.ref, "audit"));
        if (!auditSnap.empty) {
          const auditBatch = writeBatch(db);
          auditSnap.docs.forEach(d => auditBatch.delete(d.ref));
          await auditBatch.commit();
        }
      }
      const batch = writeBatch(db);
      snap.docs.forEach(d => batch.delete(d.ref));
      await batch.commit();
    }
  }, [gymId, deleteAttributed]);

  // Backfill start numbers for existing registrations that don't have one.
  // Called when an admin enables `useStartNumbers` on a comp where people
  // already registered. Sorts by registeredAt ASC (then displayName) so the
  // assignment order is deterministic and matches arrival order. Runs inside
  // a transaction with the comp doc so concurrent live registrations can't
  // collide on `nextStartNumber`. Skips registrations that already have a
  // number (idempotent — safe to call repeatedly). Returns the number of
  // registrations that received a new start number.
  const backfillStartNumbers = useCallback(async (compId) => {
    if (!gymId || !compId) return 0;
    const basePath = `gyms/${gymId}/competitions/${compId}`;
    const compRef = doc(db, basePath);
    const regsCol = collection(db, basePath, "registrations");
    const regsSnap = await getDocs(query(regsCol, where("role", "==", "competitor")));
    // Numbers already taken by competitors who have one. A number freed by a
    // cancelled registration is simply absent here, so gap-filling below
    // reuses it instead of always growing past the max.
    const usedNumbers = new Set();
    regsSnap.docs.forEach(d => {
      const sn = d.data().startNumber;
      if (Number.isInteger(sn) && sn > 0) usedNumbers.add(sn);
    });
    const missing = regsSnap.docs
      .filter(d => {
        const sn = d.data().startNumber;
        return sn === null || sn === undefined;
      })
      .sort((a, b) => {
        const ad = a.data();
        const bd = b.data();
        const at = ad.registeredAt?.toMillis ? ad.registeredAt.toMillis() : 0;
        const bt = bd.registeredAt?.toMillis ? bd.registeredAt.toMillis() : 0;
        if (at !== bt) return at - bt;
        return compareNames(ad.displayName, bd.displayName, "hu");
      });
    if (missing.length === 0) return 0;

    // One id for the whole backfill, computed OUTSIDE the transaction so a
    // retry reuses it: the audit trigger collapses every registration touched
    // here into a single "N start numbers assigned" entry.
    const bulkOp = { id: `startNumbers_${compId}_${Date.now()}`, count: missing.length };

    return await runTransaction(db, async (tx) => {
      const compSnap = await tx.get(compRef);
      let highWater = compSnap.data()?.nextStartNumber || 0;
      // Re-read each missing reg inside the tx so we don't overwrite a number
      // that a concurrent registration just assigned.
      const refs = missing.map(d => d.ref);
      const fresh = await Promise.all(refs.map(r => tx.get(r)));
      const used = new Set(usedNumbers);
      let assigned = 0;
      for (let i = 0; i < fresh.length; i++) {
        const snap = fresh[i];
        if (!snap.exists()) continue;
        const cur = snap.data().startNumber;
        if (cur !== null && cur !== undefined) {
          if (Number.isInteger(cur) && cur > 0) used.add(cur);
          continue;
        }
        const num = lowestFreeStartNumber(used);
        used.add(num);
        tx.update(snap.ref, withActor({ startNumber: num }, bulkOp));
        if (num > highWater) highWater = num;
        assigned += 1;
      }
      // `nextStartNumber` is a derived high-water mark, not a tracked key — the
      // stamp is here because the rules pin demands it on every comp-doc write,
      // not because it should produce a log entry.
      if (assigned > 0) tx.update(compRef, withActor({ nextStartNumber: highWater }));
      return assigned;
    });
  }, [gymId, withActor]);

  return {
    registerCompetitor, registerJudge, registerOrganizer, unregister,
    updateRegistration, editRegistration, updateEntryFeeStatus, updatePaymentMethod,
    registerGuestCompetitor, updateGuestCompetitor, unregisterGuest,
    saveParentBilling, saveBillingToProfile,
    loadBillingExportData, loadRegistrationContact,
    backfillStartNumbers,
  };
}
